import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Package, Plus } from 'lucide-react';
import { apiRequests } from '../../shared/api/apiRequests';
import { Product } from '../../entities/Product';
import { Pagination } from '../../features/Pagination/index.js';
import { useUrlPagination } from '../../shared/hooks/useUrlPagination.js';
import { PageBar } from '../../shared/ui/PageBar';
import { EmptyState } from '../../shared/ui/EmptyState';

const PAGE_SIZE = 8;
const BASE_PATH = '/shop/products';

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { page, offset, pageSize, setPage } = useUrlPagination(PAGE_SIZE);
  // Применённые поиск и фильтр — в URL, по ним грузятся страницы
  const query = searchParams.get('q') || '';
  const categoryId = searchParams.get('categoryId') || null;

  const [products, setProducts] = useState(null);
  const [count, setCount] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [categories, setCategories] = useState([]);
  // Черновики в полях
  const [searchValue, setSearchValue] = useState(query);
  const [filterValues, setFilterValues] = useState({ categoryId });

  useEffect(() => setSearchValue(query), [query]);
  useEffect(() => setFilterValues({ categoryId }), [categoryId]);

  useEffect(() => {
    apiRequests.products.getCategories(200).then((res) => setCategories(res.items || []));
  }, []);

  useEffect(() => {
    let cancelled = false;
    apiRequests.products.filter(pageSize, offset, query, categoryId).then((res) => {
      if (cancelled) return;
      setProducts(res.items || []);
      setCount(res.totalCount || 0);
    });
    return () => {
      cancelled = true;
    };
  }, [page, query, categoryId, reloadKey]);

  // Записать поиск/фильтр в URL и вернуться на первую страницу
  const applyParams = (values) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      Object.entries(values).forEach(([key, value]) => {
        if (value) params.set(key, value);
        else params.delete(key);
      });
      params.delete('page');
      return params;
    });
  };

  const handleSearch = () => applyParams({ q: searchValue.trim(), categoryId: filterValues.categoryId });

  const applyFilters = () => applyParams({ q: searchValue.trim(), categoryId: filterValues.categoryId });

  const resetFilters = () => applyParams({ q: null, categoryId: null });

  const handleDeleted = () => {
    if (products?.length === 1 && page > 1) setPage(page - 1);
    else setReloadKey((k) => k + 1);
  };

  const filters = [
    {
      key: 'categoryId',
      type: 'select',
      label: 'Категория',
      value: filterValues.categoryId,
      placeholder: 'Все категории',
      options: categories.map((c) => ({ value: c.id, label: c.title })),
    },
  ];

  const isFiltered = Boolean(query || categoryId);

  const renderEmpty = () => {
    if (isFiltered) {
      return (
        <EmptyState
          icon={Package}
          variant="search"
          title="Товары не найдены"
          description={
            query
              ? `По запросу «${query}» ничего нет. Попробуйте другое название или сбросьте фильтр по категории.`
              : 'В этой категории пока нет товаров. Выберите другую или сбросьте фильтр.'
          }
        />
      );
    }
    return (
      <EmptyState
        icon={Package}
        title="Витрина пока пустая"
        description="Добавьте первый товар: обложку, цену и описание. Модификаторы вроде размера и цвета настраиваются после создания."
        action={{ label: 'Добавить товар', to: `${BASE_PATH}/create`, icon: Plus }}
      />
    );
  };

  return (
    <>
      <PageBar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSearch={handleSearch}
        searchPlaceholder="Поиск товаров..."
        filters={filters}
        onFilterChange={(key, value) => setFilterValues((prev) => ({ ...prev, [key]: value }))}
        onApplyFilters={applyFilters}
        onResetFilters={resetFilters}
        createLink={`${BASE_PATH}/create`}
      />

      <div className="page-goods mb-3">
        {products?.length === 0 ? (
          renderEmpty()
        ) : (
          <div className="row">
            {products?.map((i) => (
              <Product
                key={i.id}
                data={i}
                deleteHandler={handleDeleted}
              />
            ))}
          </div>
        )}
      </div>

      <Pagination page={page} total={count} pageSize={pageSize} onPageChange={setPage} />
    </>
  );
};

export default ProductsPage;
